import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ArrowLeft, Plus, Pencil, Trash2, Settings2, Search, Database
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import fetchWithAuth from '../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');
const MATERIAL_FORMS = [
  'Sheet','Plate','Pipe','Round_Bar','Hex_Bar','Square_Bar','Flat_Bar',
  'Angle','Channel','Beam','Wire','Coil','Strip','Block','Custom'
];
const PRIMARY_INPUTS = [
  { value: 'surface_area', label: 'Surface Area (m²)' },
  { value: 'length', label: 'Cut Length (mm)' },
  { value: 'weight', label: 'Weight (kg)' },
  { value: 'volume', label: 'Volume (m³)' },
];
const LINEAR_FORMS = new Set(['Pipe','Round_Bar','Hex_Bar','Square_Bar','Flat_Bar','Angle','Channel','Beam','Wire','Coil','Strip']);
const AREA_FORMS = new Set(['Sheet','Plate']);

const formLabel = f => f.replace(/_/g, ' ');

const emptyForm = {
  profile_name: '', profile_code: '', material_grade: '', material_form: 'Sheet',
  primary_input: 'surface_area', density_kg_m3: '', thickness_mm: '', stock_length_mm: '',
  stock_width_mm: '', stock_od_mm: '', stock_wall_mm: '', linear_mass_kg_per_m: '',
  outer_perimeter_mm: '', stock_cross_section_mm2: '', nesting_buffer_pct: '30',
  kerf_width_mm: '', kerf_loss_mm: '', min_remnant_mm: '', end_drop_mm: '',
  cost_per_stock_unit: '', cost_per_kg: '',
};

const defaultPrimaryInput = (form) => LINEAR_FORMS.has(form) ? 'length' : AREA_FORMS.has(form) ? 'surface_area' : 'weight';

const MaterialProfilesPage = () => {
  
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formFilter, setFormFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchProfiles = useCallback(async () => {
    const tk = getToken(); if (!tk) return;
    setLoading(true);
    try {
      let url = `${API}/api/v1/bom/material-profiles`;
      if (formFilter) url += `?material_form=${formFilter}`;
      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${tk}` } });
      if (res.ok) setProfiles(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [formFilter]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      profile_name: p.profile_name || '',
      profile_code: p.profile_code || '',
      material_grade: p.material_grade || '',
      material_form: p.material_form || 'Sheet',
      primary_input: p.primary_input || defaultPrimaryInput(p.material_form || 'Sheet'),
      density_kg_m3: p.density_kg_m3?.toString() || '',
      thickness_mm: p.thickness_mm?.toString() || '',
      stock_length_mm: p.stock_length_mm?.toString() || '',
      stock_width_mm: p.stock_width_mm?.toString() || '',
      stock_od_mm: p.stock_od_mm?.toString() || '',
      stock_wall_mm: p.stock_wall_mm?.toString() || '',
      linear_mass_kg_per_m: p.linear_mass_kg_per_m?.toString() || '',
      outer_perimeter_mm: p.outer_perimeter_mm?.toString() || '',
      stock_cross_section_mm2: p.stock_cross_section_mm2?.toString() || '',
      nesting_buffer_pct: p.nesting_buffer_pct?.toString() || '30',
      kerf_width_mm: p.kerf_width_mm?.toString() || '',
      kerf_loss_mm: p.kerf_loss_mm?.toString() || '',
      min_remnant_mm: p.min_remnant_mm?.toString() || '',
      end_drop_mm: p.end_drop_mm?.toString() || '',
      cost_per_stock_unit: p.cost_per_stock_unit?.toString() || '',
      cost_per_kg: p.cost_per_kg?.toString() || '',
    });
    setDialogOpen(true);
  };

  const handleFormChange = (newForm) => {
    setForm(f => ({
      ...f,
      material_form: newForm,
      primary_input: defaultPrimaryInput(newForm),
    }));
  };

  const handleSave = async () => {
    if (!form.profile_name || !form.profile_code || !form.material_grade || !form.density_kg_m3) {
      toast.error('Name, Code, Grade, and Density are required'); return;
    }
    setSaving(true);
    try {
      const body = {
        profile_name: form.profile_name,
        material_grade: form.material_grade,
        material_form: form.material_form,
        primary_input: form.primary_input,
        density_kg_m3: parseFloat(form.density_kg_m3),
        nesting_buffer_pct: parseFloat(form.nesting_buffer_pct || '30'),
      };
      if (!editing) body.profile_code = form.profile_code;
      // Optional numeric fields
      const optionalFloats = {
        thickness_mm: form.thickness_mm,
        stock_length_mm: form.stock_length_mm,
        stock_width_mm: form.stock_width_mm,
        stock_od_mm: form.stock_od_mm,
        stock_wall_mm: form.stock_wall_mm,
        linear_mass_kg_per_m: form.linear_mass_kg_per_m,
        outer_perimeter_mm: form.outer_perimeter_mm,
        stock_cross_section_mm2: form.stock_cross_section_mm2,
        kerf_width_mm: form.kerf_width_mm,
        kerf_loss_mm: form.kerf_loss_mm,
        min_remnant_mm: form.min_remnant_mm,
        end_drop_mm: form.end_drop_mm,
        cost_per_stock_unit: form.cost_per_stock_unit,
        cost_per_kg: form.cost_per_kg,
      };
      Object.entries(optionalFloats).forEach(([k, v]) => {
        if (v) body[k] = parseFloat(v);
      });

      const url = editing
        ? `${API}/api/v1/bom/material-profiles/${editing.profile_id}`
        : `${API}/api/v1/bom/material-profiles`;
      const res = await fetchWithAuth(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success(editing ? 'Profile updated (new version)' : 'Profile created');
      setDialogOpen(false); fetchProfiles();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async (p) => {
    if (!window.confirm(`Deactivate profile ${p.profile_code}?`)) return;
    await fetchWithAuth(`${API}/api/v1/bom/material-profiles/${p.profile_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
    toast.success('Profile deactivated'); fetchProfiles();
  };

  const handleSeed = async () => {
    if (!window.confirm('Seed common linear stock profiles (pipes, bars, structural, wire)? Existing profiles will not be overwritten.')) return;
    setSeeding(true);
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/material-profiles/seed`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Seeded: ${result.created} created, ${result.skipped} already existed`);
        fetchProfiles();
      }
    } catch (e) { toast.error('Seed failed'); }
    finally { setSeeding(false); }
  };

  const filtered = profiles.filter(p =>
    !search ||
    p.profile_code.toLowerCase().includes(search.toLowerCase()) ||
    p.profile_name.toLowerCase().includes(search.toLowerCase()) ||
    p.material_grade.toLowerCase().includes(search.toLowerCase())
  );

  const isLinear = LINEAR_FORMS.has(form.material_form);
  const isArea = AREA_FORMS.has(form.material_form);
  const isPipe = form.material_form === 'Pipe';
  const isBar = ['Round_Bar','Hex_Bar','Square_Bar','Flat_Bar'].includes(form.material_form);
  const isStructural = ['Angle','Channel','Beam'].includes(form.material_form);

  const inputBadge = (pi) => {
    if (pi === 'length') return <Badge variant="outline" className="text-xs border-teal-500/30 text-teal-400">Length</Badge>;
    if (pi === 'surface_area') return <Badge variant="outline" className="text-xs border-blue-500/30 text-blue-400">Area</Badge>;
    return <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">{pi}</Badge>;
  };

  const dimensionSummary = (p) => {
    const parts = [];
    if (p.stock_od_mm) parts.push(`OD ${p.stock_od_mm}mm`);
    if (p.stock_wall_mm) parts.push(`wall ${p.stock_wall_mm}mm`);
    if (p.thickness_mm) parts.push(`${p.thickness_mm}mm`);
    if (p.stock_width_mm && !AREA_FORMS.has(p.material_form)) parts.push(`W ${p.stock_width_mm}mm`);
    if (p.stock_length_mm) parts.push(`L ${(p.stock_length_mm / 1000).toFixed(1)}m`);
    return parts.length ? parts.join(' / ') : '—';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/bom')} className="text-slate-400" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2"><Settings2 className="w-5 h-5 text-amber-400" /> Material Conversion Profiles</h1>
              <p className="text-slate-400 text-xs mt-0.5">Define raw material physics — sheets, pipes, bars, structural, wire [8.1.2]</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleSeed} disabled={seeding} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="seed-profiles-btn">
              <Database className="w-4 h-4 mr-1.5" />{seeding ? 'Seeding...' : 'Seed Linear Stock'}
            </Button>
            <Button onClick={openCreate} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="new-profile-btn"><Plus className="w-4 h-4 mr-1.5" /> New Profile</Button>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input placeholder="Search profiles..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500" data-testid="search-profiles-input" />
          </div>
          <Select value={formFilter} onValueChange={v => setFormFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[170px] bg-slate-800/60 border-slate-700 text-white" data-testid="form-filter">
              <SelectValue placeholder="All Forms" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700 text-white">
              <SelectItem value="all">All Forms</SelectItem>
              {MATERIAL_FORMS.map(f => <SelectItem key={f} value={f}>{formLabel(f)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl overflow-hidden" data-testid="profiles-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700/60 text-slate-400 text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Grade</th>
                <th className="text-left px-4 py-3">Form</th>
                <th className="text-center px-3 py-3">Input</th>
                <th className="text-left px-4 py-3">Dimensions</th>
                <th className="text-right px-4 py-3">kg/m</th>
                <th className="text-right px-4 py-3">Cost</th>
                <th className="text-center px-3 py-3">V</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={10} className="px-5 py-10 text-center text-slate-500">Loading...</td></tr> :
               filtered.length === 0 ? <tr><td colSpan={10} className="px-5 py-10 text-center text-slate-500">
                 {profiles.length === 0 ? 'No profiles. Click "Seed Linear Stock" to populate common profiles.' : 'No matching profiles'}
               </td></tr> :
               filtered.map(p => (
                <tr key={p.profile_id} className="border-b border-slate-700/30 hover:bg-slate-700/20" data-testid={`profile-row-${p.profile_code}`}>
                  <td className="px-4 py-3 font-mono text-cyan-400 font-medium text-xs">{p.profile_code}</td>
                  <td className="px-4 py-3 text-white text-xs">{p.profile_name}</td>
                  <td className="px-4 py-3 text-slate-300 text-xs">{p.material_grade}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={`text-xs ${LINEAR_FORMS.has(p.material_form) ? 'border-teal-500/30 text-teal-400' : 'border-blue-500/30 text-blue-400'}`}>
                      {formLabel(p.material_form)}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-center">{inputBadge(p.primary_input || (LINEAR_FORMS.has(p.material_form) ? 'length' : 'surface_area'))}</td>
                  <td className="px-4 py-3 text-xs text-slate-400 font-mono">{dimensionSummary(p)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300 text-xs">{p.linear_mass_kg_per_m ? `${p.linear_mass_kg_per_m}` : '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300 text-xs">{p.cost_per_stock_unit ? `₹${p.cost_per_stock_unit.toLocaleString()}` : '—'}</td>
                  <td className="px-3 py-3 text-center"><Badge className="bg-slate-700 text-slate-300 text-xs">v{p.version}</Badge></td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-white" onClick={() => openEdit(p)} data-testid={`edit-${p.profile_code}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-400" onClick={() => handleDeactivate(p)} data-testid={`delete-${p.profile_code}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-xs text-slate-600 flex items-center gap-1.5">
          <span className="text-amber-600/60">[8.1.2]</span> Profiles are versioned and audited. Updates create new versions.
          <span className="ml-3 text-teal-600/60">[8.5.1]</span> Linear stock profiles support pipe, bar, structural, wire material forms.
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5 text-amber-400" />{editing ? 'Edit Profile' : 'Create Profile'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {/* Code + Name */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-400">Profile Code *</Label><Input value={form.profile_code} onChange={e => setForm(f => ({...f, profile_code: e.target.value.toUpperCase()}))} disabled={!!editing} placeholder="PIPE-SS-1-SCH40" className="bg-slate-800 border-slate-600 text-white mt-1 font-mono text-xs" data-testid="input-profile-code" /></div>
              <div><Label className="text-xs text-slate-400">Profile Name *</Label><Input value={form.profile_name} onChange={e => setForm(f => ({...f, profile_name: e.target.value}))} placeholder="SS316L Pipe 1&quot; NB Sch 40" className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="input-profile-name" /></div>
            </div>
            {/* Grade + Form + Primary Input */}
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-slate-400">Grade *</Label><Input value={form.material_grade} onChange={e => setForm(f => ({...f, material_grade: e.target.value}))} placeholder="SS316L" className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="input-grade" /></div>
              <div><Label className="text-xs text-slate-400">Form *</Label>
                <Select value={form.material_form} onValueChange={handleFormChange}>
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="select-form"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-60">{MATERIAL_FORMS.map(f => <SelectItem key={f} value={f}>{formLabel(f)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-400">Primary Input</Label>
                <Select value={form.primary_input} onValueChange={v => setForm(f => ({...f, primary_input: v}))}>
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="select-primary-input"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 text-white">{PRIMARY_INPUTS.map(pi => <SelectItem key={pi.value} value={pi.value}>{pi.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {/* Density */}
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-slate-400">Density (kg/m³) *</Label><Input type="number" value={form.density_kg_m3} onChange={e => setForm(f => ({...f, density_kg_m3: e.target.value}))} placeholder="7950" className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="input-density" /></div>
              {isLinear && (
                <div><Label className="text-xs text-slate-400">Linear Mass (kg/m)</Label><Input type="number" value={form.linear_mass_kg_per_m} onChange={e => setForm(f => ({...f, linear_mass_kg_per_m: e.target.value}))} placeholder="2.50" className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="input-linear-mass" /></div>
              )}
              <div><Label className="text-xs text-slate-400">Nesting/Waste Buffer %</Label><Input type="number" value={form.nesting_buffer_pct} onChange={e => setForm(f => ({...f, nesting_buffer_pct: e.target.value}))} placeholder="30" className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="input-buffer" /></div>
            </div>

            {/* Form-specific dimensions */}
            {isArea && (
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-slate-400">Thickness (mm)</Label><Input type="number" value={form.thickness_mm} onChange={e => setForm(f => ({...f, thickness_mm: e.target.value}))} placeholder="3.0" className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="input-thickness" /></div>
                <div><Label className="text-xs text-slate-400">Stock Length (mm)</Label><Input type="number" value={form.stock_length_mm} onChange={e => setForm(f => ({...f, stock_length_mm: e.target.value}))} placeholder="2440" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
                <div><Label className="text-xs text-slate-400">Stock Width (mm)</Label><Input type="number" value={form.stock_width_mm} onChange={e => setForm(f => ({...f, stock_width_mm: e.target.value}))} placeholder="1220" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
              </div>
            )}

            {isPipe && (
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-slate-400">OD (mm) *</Label><Input type="number" value={form.stock_od_mm} onChange={e => setForm(f => ({...f, stock_od_mm: e.target.value}))} placeholder="33.4" className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="input-od" /></div>
                <div><Label className="text-xs text-slate-400">Wall (mm)</Label><Input type="number" value={form.stock_wall_mm} onChange={e => setForm(f => ({...f, stock_wall_mm: e.target.value}))} placeholder="3.38" className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="input-wall" /></div>
                <div><Label className="text-xs text-slate-400">Stock Length (mm)</Label><Input type="number" value={form.stock_length_mm} onChange={e => setForm(f => ({...f, stock_length_mm: e.target.value}))} placeholder="6000" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
              </div>
            )}

            {isBar && (
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-slate-400">{form.material_form === 'Flat_Bar' ? 'Width (mm)' : 'OD / Size (mm)'}</Label><Input type="number" value={form.material_form === 'Flat_Bar' ? form.stock_width_mm : form.stock_od_mm} onChange={e => setForm(f => form.material_form === 'Flat_Bar' ? ({...f, stock_width_mm: e.target.value}) : ({...f, stock_od_mm: e.target.value}))} placeholder={form.material_form === 'Flat_Bar' ? '50' : '25'} className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
                {form.material_form === 'Flat_Bar' && (
                  <div><Label className="text-xs text-slate-400">Thickness (mm)</Label><Input type="number" value={form.thickness_mm} onChange={e => setForm(f => ({...f, thickness_mm: e.target.value}))} placeholder="6" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
                )}
                <div><Label className="text-xs text-slate-400">Stock Length (mm)</Label><Input type="number" value={form.stock_length_mm} onChange={e => setForm(f => ({...f, stock_length_mm: e.target.value}))} placeholder="3000" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
              </div>
            )}

            {isStructural && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-400">Stock Length (mm)</Label><Input type="number" value={form.stock_length_mm} onChange={e => setForm(f => ({...f, stock_length_mm: e.target.value}))} placeholder="6000" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
                <div><Label className="text-xs text-slate-400">Cross-section (mm²)</Label><Input type="number" value={form.stock_cross_section_mm2} onChange={e => setForm(f => ({...f, stock_cross_section_mm2: e.target.value}))} placeholder="475" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
              </div>
            )}

            {(form.material_form === 'Wire' || form.material_form === 'Coil') && (
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-slate-400">Diameter (mm)</Label><Input type="number" value={form.stock_od_mm} onChange={e => setForm(f => ({...f, stock_od_mm: e.target.value}))} placeholder="1.78" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
                <div><Label className="text-xs text-slate-400">Spool Length (mm)</Label><Input type="number" value={form.stock_length_mm} onChange={e => setForm(f => ({...f, stock_length_mm: e.target.value}))} placeholder="100000" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
              </div>
            )}

            {/* Linear stock: outer perimeter for surface treatment calc */}
            {isLinear && !isPipe && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-400">Outer Perimeter (mm)</Label><Input type="number" value={form.outer_perimeter_mm} onChange={e => setForm(f => ({...f, outer_perimeter_mm: e.target.value}))} placeholder="Auto or manual" className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="input-perimeter" /></div>
              </div>
            )}

            {/* Kerf / Waste */}
            {isLinear && (
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-slate-400">Kerf Loss (mm)</Label><Input type="number" value={form.kerf_loss_mm} onChange={e => setForm(f => ({...f, kerf_loss_mm: e.target.value}))} placeholder="3" className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="input-kerf" /></div>
                <div><Label className="text-xs text-slate-400">Min Remnant (mm)</Label><Input type="number" value={form.min_remnant_mm} onChange={e => setForm(f => ({...f, min_remnant_mm: e.target.value}))} placeholder="50" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
                <div><Label className="text-xs text-slate-400">End Drop (mm)</Label><Input type="number" value={form.end_drop_mm} onChange={e => setForm(f => ({...f, end_drop_mm: e.target.value}))} placeholder="10" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
              </div>
            )}
            {isArea && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-400">Kerf Width (mm)</Label><Input type="number" value={form.kerf_width_mm} onChange={e => setForm(f => ({...f, kerf_width_mm: e.target.value}))} placeholder="0.2" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
                <div><Label className="text-xs text-slate-400">Edge Margin (mm)</Label><Input type="number" value={form.min_edge_margin_mm} onChange={e => setForm(f => ({...f, min_edge_margin_mm: e.target.value}))} placeholder="10" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
              </div>
            )}

            {/* Cost */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-400">Cost per Stock Unit (₹)</Label><Input type="number" value={form.cost_per_stock_unit} onChange={e => setForm(f => ({...f, cost_per_stock_unit: e.target.value}))} placeholder="18500" className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="input-cost-unit" /></div>
              <div><Label className="text-xs text-slate-400">Cost per kg (₹)</Label><Input type="number" value={form.cost_per_kg} onChange={e => setForm(f => ({...f, cost_per_kg: e.target.value}))} placeholder="0" className="bg-slate-800 border-slate-600 text-white mt-1" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="save-profile-btn">{saving ? 'Saving...' : editing ? 'Update (New Version)' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialProfilesPage;
